import { stableStringify } from "../utils/stableStringify";

// Keep `logging: false` and no `api_key`, or ThumbmarkJS sends telemetry to api.thumbmarkjs.com.

const TIMEOUT_MS = 2000;

// Bump when the curated shape below changes so the backend can tell versions apart.
const COMPONENTS_SCHEMA_VERSION = 1;

interface Fingerprint {
  hash: string;
  data: string; // base64url JSON of the curated components, or ""
}

// Cache the promise so the fingerprint is computed once per page load and shared by
// every request. `settled` holds the last real result for the synchronous,
// non-blocking peek requestContext uses.
let cached: Promise<Fingerprint> | null = null;
let settled: Fingerprint | null = null;

function get(): Promise<Fingerprint> {
  if (!cached) {
    const p = compute();
    cached = p;
    // A transient failure (component timeout, thrown library error) resolves to an
    // empty result. Don't let that stick for the whole page load and disable
    // fingerprinting with no retry: drop the cache so the next request recomputes.
    // A real result, including one with a hash but no component data, stays
    // cached and shared. Guard on identity so a concurrent retry isn't clobbered.
    void p.then((fp) => {
      if (fp.hash === "") {
        if (cached === p) cached = null;
      } else {
        settled = fp;
      }
    });
  }
  return cached;
}

export function getClientFingerprint(): Promise<string> {
  return get().then((fp) => fp.hash);
}

export function getClientFingerprintData(): Promise<string> {
  return get().then((fp) => fp.data);
}

// Non-blocking accessor for requestContext: returns the fingerprint only
// once the one-shot computation has settled, else null (kicking off / retrying the
// computation in the background without ever awaiting it). This keeps fingerprinting
// off the request critical path. A request that races ahead of the first result
// simply goes out without the headers. The backend records an empty initial
// footprint and promotes it in place once a later, fingerprinted request arrives, so
// no spurious "fingerprint changed" event results.
export function peekClientFingerprint(): Fingerprint | null {
  if (!settled) void get();
  return settled;
}

function timeout(ms: number): Promise<Fingerprint> {
  return new Promise((resolve) => setTimeout(() => resolve({ hash: "", data: "" }), ms));
}

async function compute(): Promise<Fingerprint> {
  try {
    const { Thumbmark } = await import("@thumbmarkjs/thumbmarkjs");
    const tm = new Thumbmark({ logging: false, timeout: TIMEOUT_MS });
    // Cap total time so a slow/hanging component never blocks API requests.
    const result = await Promise.race([
      tm.get().then(async (r) => ({
        hash: typeof r?.thumbmark === "string" ? r.thumbmark : "",
        data: await encodeComponents(r?.components),
      })),
      timeout(TIMEOUT_MS),
    ]);
    return result;
  } catch {
    // Never surface an error into the request pipeline, just send nothing.
    return { hash: "", data: "" };
  }
}

// encodeComponents picks a curated, stable subset off the raw ThumbmarkJS
// components and returns it as base64url JSON. Full system/hardware/locales objects
// are kept verbatim (readable identity axes); the opaque high-entropy components are
// reduced to sub-hashes for "what changed" diffing. Noisy/low-signal groups (math,
// plugins, webrtc, speech, screen.mediaMatches) are dropped. Returns "" on any error.
async function encodeComponents(components: unknown): Promise<string> {
  try {
    if (!components || typeof components !== "object") return "";
    const c = components as Record<string, any>;

    const curated: Record<string, unknown> = {
      v: COMPONENTS_SCHEMA_VERSION,
      system: c.system,
      hardware: c.hardware,
      locales: c.locales,
      screen: {
        is_touchscreen: c.screen?.is_touchscreen,
        maxTouchPoints: c.screen?.maxTouchPoints,
        colorDepth: c.screen?.colorDepth,
      },
      h: {
        canvas: c.canvas?.commonPixelsHash ?? "",
        webgl: c.webgl?.commonPixelsHash ?? "",
        // Round the audio float so floating-point noise doesn't churn the value.
        // Omit (undefined → dropped by JSON.stringify) when absent. Safari/Brave
        // exclude audio FP, and emitting "" here is a type mismatch against the
        // backend's numeric Audio field that would discard the whole blob on parse.
        audio: typeof c.audio?.sampleHash === "number" ? Math.round(c.audio.sampleHash * 1000) / 1000 : undefined,
        // The fonts component is a per-font width map; store a single hash of it
        // rather than the whole dictionary.
        fonts: await sha256Hex(stableStringify(c.fonts)),
      },
    };

    return base64urlEncode(JSON.stringify(curated));
  } catch {
    return "";
  }
}

async function sha256Hex(input: string): Promise<string> {
  try {
    const bytes = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 32);
  } catch {
    return "";
  }
}

function base64urlEncode(json: string): string {
  // JSON here is ASCII (UA strings, timezone names, hashes), but guard unicode.
  const b64 = btoa(unescape(encodeURIComponent(json)));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
